import type {
  TTour,
  TTourPlacement,
  TTourRegistry,
  TTourResponse,
  TTourStep,
  TTourStepResponse,
  TTourTrigger,
} from '../types';
import { isTourAnchorMounted } from './anchors';

const toTrigger = (trigger?: string): TTourTrigger => (trigger === 'manual' ? 'manual' : 'auto');

const toPlacement = (placement?: string): TTourPlacement =>
  placement?.startsWith('top') ? 'top' : 'bottom';

const toStep = (step: TTourStepResponse): TTourStep => ({
  target: step.target,
  title: step.title ?? '',
  content: step.content ?? '',
  placement: toPlacement(step.placement),
  roles: step.roles ?? [],
});

/** Fills in what the backend leaves optional, so the rest of the module can rely on it. */
const toTour = (tour: TTourResponse): TTour | null => {
  const steps = (Array.isArray(tour?.steps) ? tour.steps : []).filter(step => !!step?.target);

  if (!tour?.tourId || !steps.length) {
    return null;
  }

  return {
    id: tour.tourId,
    label: tour.label ?? tour.tourId,
    version: tour.version ?? 1,
    enabled: tour.enabled !== false,
    trigger: toTrigger(tour.trigger),
    roles: tour.roles ?? [],
    routes: tour.routes ?? [],
    steps: steps.map(toStep),
  };
};

export const toTourRegistry = (tours: TTourResponse[] | undefined | null): TTourRegistry =>
  (Array.isArray(tours) ? tours : []).reduce<TTourRegistry>((registry, tour) => {
    const normalized = toTour(tour);

    return normalized ? { ...registry, [normalized.id]: normalized } : registry;
  }, {});

export const isTourVisibleForRole = (tour: TTour, role: string | null): boolean =>
  tour.enabled && !!role && tour.roles.includes(role);

const isStepVisibleForRole = (step: TTourStep, role: string | null): boolean =>
  !step.roles.length || (!!role && step.roles.includes(role));

export const isTourOnRoute = (tour: TTour, routeName: string | null): boolean =>
  !tour.routes.length || (!!routeName && tour.routes.some(route => routeName.startsWith(route)));

export const getPlayableSteps = (tour: TTour, role: string | null): TTourStep[] =>
  tour.steps
    .filter(step => isStepVisibleForRole(step, role))
    .filter(step => isTourAnchorMounted(step.target));

const getRouteMatchLength = (tour: TTour, routeName: string): number =>
  tour.routes.reduce(
    (longest, route) =>
      routeName.startsWith(route) && route.length > longest ? route.length : longest,
    0,
  );

export const getAutoStartTour = (
  tours: TTour[],
  role: string | null,
  routeName: string | null,
): TTour | null => {
  if (!routeName) {
    return null;
  }

  return tours
    .filter(
      tour =>
        tour.trigger === 'auto' &&
        tour.routes.length > 0 &&
        isTourVisibleForRole(tour, role) &&
        isTourOnRoute(tour, routeName),
    )
    .reduce<TTour | null>(
      (candidate, tour) =>
        !candidate || getRouteMatchLength(tour, routeName) > getRouteMatchLength(candidate, routeName)
          ? tour
          : candidate,
      null,
    );
};

export const getReplayableTours = (tours: TTourRegistry, role: string | null): TTour[] =>
  Object.values(tours).filter(tour => isTourVisibleForRole(tour, role));
